/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback } from "react";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Form, Space } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// Redux-hooks:
import { useAppSelector } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getAppState, getLng, getUser } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type Tbtype = {
  type:"link" | "text" | "default" | "dashed" | "primary" | undefined,
  shape:"default" | "circle" | "round",
  size:"small" | "middle" | "large",
  danger:boolean,
  ghost:boolean,
  block:boolean
};

const { rz } = window;

const RZbuttonsGroup:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZbuttonsGroup.displayName = "ButtonsGroup";
  const user = useAppSelector(getUser);
  const lng = useAppSelector(getLng);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, size} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  // const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState?.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass, elState?.disabled, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState?.userStyle, elState?.visible, permissions]);

  const btype = useMemo<Tbtype>(() => {
    const typ:Tbtype = {type: "default", shape: "default", size: "middle", danger: false, ghost: false, block: false};
    (type || "").toLowerCase().replace((/(\s|_|\|)/igm), "-").split("-").forEach((tt) => {
      if (~["default", "dashed", "primary", "text", "link"].indexOf(tt)) { typ.type = tt }
      else if (tt === "danger") { typ.danger = true }
      else if (tt === "ghost") { typ.ghost = true }
      else if (~["block", "expand"].indexOf(tt)) { typ.block = true }
      else if (~["circle", "round"].indexOf(tt)) { typ.shape = tt }
      else if (~["small", "middle", "large"].indexOf(tt)) { typ.size = tt }
    });
    return typ;
  }, [type]);
  // В space.compact режиме иконки выглядели как будто уменьшаются (последние до точки). block у antd = растянуть группу на всю ширину
  const isBlock = useMemo<boolean>(() => {
    const v = (elState?.attributes?.block ?? attributes?.block);
    if (rz.isBoolean(v)) { return v; }
    if (rz.isString(v)) {
      const s = (v || "").toLowerCase().trim();
      if (~["true", "1", "yes", "on"].indexOf(s)) { return true; }
      if (~["false", "0", "no", "off"].indexOf(s)) { return false; }
    }
    return true;
  }, [elState?.attributes?.block, attributes?.block]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState.attributes["title2_" + lng] || elState.attributes.title2 || elState.attributes["title2_" + rlng]), [elState.attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // if (iType.rules) { frmItemProps.name = String(key); frmItemProps.rules = iType.rules; frmItemProps.getValueProps = () => (elState.value) }
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  return wrapperForm(
    <Space.Compact block={isBlock}
      key={key} className={clsu.join(" ")} style={styl}
      size={size || btype.size}
      uk-tooltip={tooltip}
    >
      {children}
    </Space.Compact>
  );
}

export default RZbuttonsGroup;
/* eslint-enable @typescript-eslint/no-unused-vars */
